"use client";

import { useEffect } from "react";
import { useLocale } from "next-intl";
import { isBackendConfigured } from "@/api/api-client";
import { applyBookingSessionMeToStore } from "@/lib/applyBookingSessionMeToStore";
import {
    getAzDanceBookingToken,
    isAzDanceBookingTokenValid,
} from "@/lib/azdanceBookingSession";
import { syncAzDanceBookingTokenFromCookie } from "@/lib/azdanceBookingTokenSync";
import { pullBookingParticipantsIntoStore } from "@/lib/bookingParticipantsRemoteSync";
import { usePathname } from "@/i18n/navigation";
import { useRegistrationStore } from "@/state/stores/registrationStore";

/**
 * Лейаут воронки регистрации (`FestRegistrationLayoutClient`): при смене страницы/локали
 * или `dashboardEventBookingId` в сторе — сначала `GET /azdance/profile/me`, затем
 * при наличии id брони — `await GET /azdance/booking/participants` (строго после `profile/me`).
 */
export function useFestRegistrationBookingSync() {
    const locale = useLocale();
    const language =
        typeof locale === "string" && locale.length > 0 ? locale : "en";
    const pathname = usePathname() || "/";
    const dashboardEventBookingId = useRegistrationStore(
        (s) => s.dashboardEventBookingId,
    );

    useEffect(() => {
        if (!isBackendConfigured()) return;
        let cancelled = false;
        void (async () => {
            syncAzDanceBookingTokenFromCookie();
            let token = getAzDanceBookingToken();
            if (!token || !isAzDanceBookingTokenValid()) {
                await new Promise((r) => setTimeout(r, 100));
                syncAzDanceBookingTokenFromCookie();
                token = getAzDanceBookingToken();
            }
            if (!token || !isAzDanceBookingTokenValid()) return;
            await applyBookingSessionMeToStore(language);
            if (cancelled) return;
            const eid =
                useRegistrationStore.getState().dashboardEventBookingId?.trim();
            if (!eid) return;
            await pullBookingParticipantsIntoStore(language, {
                isCancelled: () => cancelled,
            });
        })();
        return () => {
            cancelled = true;
        };
    }, [language, pathname, dashboardEventBookingId]);
}
